import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import {AuthService} from "../../services/auth/auth.service";
import {ChatMessage} from "../../models/chat-message";
import {ChatService} from "../../services/chat/chat.service";
import {Subscription, firstValueFrom, filter} from 'rxjs';
import {ConnectionOrchestrator, ConnectionState} from "../../services/connection/connection-manager.service";
import { PerformanceMetricsService } from "../../services/performance-metrics.service"; // Add this
import { ErrorTrackingService } from "../../services/error-tracking.service"; // Add this
import { ProgressTrackingService } from '../../services/websocket/progress-tracking.service';
import { ProgressUpdate } from '../../models/progress-update';
import { ConversationManager } from '../../services/conversation/conversation-manager.service';
import { HttpClient } from '@angular/common/http';
import { ChatCommunicationService } from '../../services/chat/chat-communication.service';
import { ConversationListService } from '../../services/conversation/conversation-list.service';

@Component({
    selector: 'app-chat-main',
    templateUrl: './chat-main.component.html',
    styleUrls: ['./chat-main.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class ChatMainComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input("chatMessages") chatMessages: Array<ChatMessage> = [];
  @ViewChild('conversationContainer', { static: false }) conversationContainer!: ElementRef;
  private messagesSubscription: Subscription | null = null;
  private lastBotMessageSubscription: Subscription | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private currentTrackedMessageId: string | undefined = undefined;

  // Auto-scroll management
  isUserScrolledUp = false;
  hasNewMessages = false;
  private scrollThreshold = 100; // px from bottom to consider "at bottom"
  private lastSeenMessageCount = 0;
  private pendingScrollFrame: number | null = null; // For debouncing scroll with RAF
  private connectionSubscription: Subscription | null = null;
  private connectionStatusSubscription: Subscription | null = null;

  // Track first message for performance metrics
  private hasSeenFirstBotMessage = false;
  private firstUserInteractionTracked = false;
  private hasInitializedChat = false;

  // Connection status properties
  connectionState: string = 'DISCONNECTED';
  reconnectAttempt: number = 0;
  maxAttempts: number = 20;
  isRetrying: boolean = false;
  private showConnectingBanner: boolean = false;
  private connectingTimer: any = null;

  // Conversation ID - managed by ConversationManager
  private conversationId: string = '';

  // Orphaned conversation state
  isOrphaned: boolean = false;
  orphanReason: string = '';
  private stateCheckInterval: any = null;

  // IndexedDB restoration state - prevents flicker on page reload
  isRestoringFromIndexedDB: boolean = true;

  // Chat history sidebar state (FIN-0102)
  isSidebarOpen: boolean = false;

  constructor(
    private authService: AuthService,
    private chatService: ChatService,
    private connectionManager: ConnectionOrchestrator,
    private performanceMetrics: PerformanceMetricsService,
    private errorTracking: ErrorTrackingService,
    private progressTrackingService: ProgressTrackingService, // Injected for debug
    private conversationManager: ConversationManager,
    private http: HttpClient,
    private chatCommunicationService: ChatCommunicationService,
    private conversationListService: ConversationListService  // FIN-0102: Chat history
  ) {
    // DON'T get conversation ID in constructor - wait for IndexedDB restoration to complete first
    // This will be set in initializeChatIfReady() after restoration
  }

  getUserInfo() {
    return this.authService.userAuthInfo$;
  }

  /**
   * Determines if the current message is the last bot message in the conversation
   * @param message The current message being checked
   * @param index The index of the current message
   * @returns boolean indicating if this is the last bot message
   */
  isLastBotMessage(message: ChatMessage, index: number): boolean {
    if (!message.bot) {
      return false;
    }

    // Loop through the rest of the messages after this one (now searching forward since oldest-first)
    // and check if there's another bot message
    for (let i = index + 1; i < this.chatMessages.length; i++) {
      if (this.chatMessages[i].bot) {
        // Found another bot message after this one, so this is not the last
        return false;
      }
    }

    // No more bot messages found, so this is the last one
    return true;
  }

  /**
   * Determines if the current message is the first bot message in the conversation
   * @param message The current message being checked
   * @param index The index of the current message
   * @returns boolean indicating if this is the first bot message
   */
  isFirstBotMessage(message: ChatMessage, index: number): boolean {
    if (!message.bot) {
      return false;
    }

    // Loop through messages from the beginning (oldest first, search backwards)
    for (let i = index - 1; i >= 0; i--) {
      if (this.chatMessages[i].bot) {
        // Found another bot message before this one
        return false;
      }
    }

    // No bot messages found before this one, so this is the first
    return true;
  }

  /**
   * TrackBy function for messages ngFor to prevent unnecessary component recreation
   * Uses messageId (or timestamp as fallback) to track message identity
   * @param index The index of the message in the array
   * @param message The message object
   * @returns A unique identifier for the message
   */
  trackByMessageId(index: number, message: ChatMessage): string {
    return message.messageId || message.timestamp || `msg-${index}`;
  }

  async ngOnInit(): Promise<void> {

    // Mark component initialization start
    this.performanceMetrics.markStart('chat_component_init');

    // Set up message subscription immediately
    // 🔥 FIX: Subscribe to messages FIRST, then hide loading state when messages arrive
    // This prevents content layout shift (CLS) when loading completes but messages haven't loaded yet
    this.getMessages();

    // Set up connection status monitoring
    this.connectionStatusSubscription = this.connectionManager.connectionStatus$
      .subscribe(status => {
        this.connectionState = status.state;
        this.reconnectAttempt = status.reconnectAttempt || 0;
        this.maxAttempts = status.maxReconnectAttempts || 20;

        // Handle CONNECTING state with 2-second delay
        if (status.state === ConnectionState.CONNECTING) {
          // Only show banner if connection takes >2 seconds
          this.connectingTimer = setTimeout(() => {
            this.showConnectingBanner = true;
          }, 2000);
        } else {
          // Clear timer and hide banner if we connect quickly or change state
          if (this.connectingTimer) {
            clearTimeout(this.connectingTimer);
            this.connectingTimer = null;
          }
          this.showConnectingBanner = false;
        }

        // Reset retry flag when connected
        if (status.state === ConnectionState.CONNECTED) {
          this.isRetrying = false;
          this.reconnectAttempt = 0;
        }
      });

    // 🚀 PERFORMANCE FIX: Restore from IndexedDB IMMEDIATELY, don't wait for WebSocket!
    // IndexedDB loads in ~100-200ms, WebSocket can take 5+ seconds
    console.log('[CHAT-MAIN] Restoring from IndexedDB immediately (not waiting for WebSocket)');

    try {
      // Wait for IndexedDB restoration to complete
      await firstValueFrom(this.chatService.restorationComplete$.pipe(
        filter(complete => complete === true)
      ));

      console.log('[CHAT-MAIN] ✅ IndexedDB restoration complete, checking for conversation');

      // Check if we restored an existing conversation
      const existingConversationId = this.conversationManager.getActiveConversationId();

      // 🔥 FIX (FIN-0068): Eliminate dual implementation - always restore if conversation ID exists
      // The state machine in ConversationManager handles whether it's a true restoration or orphan detection
      // Message count may be 0 (Tab 2 timing), but we still need to restore to trigger proper sync

      // Hide loading state based on actual message availability
      if (this.chatMessages.length > 0) {
        // Fast-path: messages were restored from localStorage cache before subscription
        console.log('[CHAT-MAIN] Messages already loaded from cache, hiding loading state immediately');
        this.isRestoringFromIndexedDB = false;
      } else if (!existingConversationId) {
        // No conversation exists, hide loading state
        console.log('[CHAT-MAIN] No existing conversation, hiding loading state immediately');
        this.isRestoringFromIndexedDB = false;
      } else {
        // Conversation exists but messages not loaded yet (slow-path or Tab 2)
        console.log('[CHAT-MAIN] Conversation exists, will hide loading state when messages arrive');
      }

      if (existingConversationId) {
        console.log('[CHAT-MAIN] Restoring conversation from IndexedDB', {
          conversationId: existingConversationId,
          messageCount: this.chatMessages.length
        });

        // Set conversation ID so UI knows which conversation is active
        this.conversationId = existingConversationId;

        // 🔥 FIX: Register conversation IMMEDIATELY so canSendMessages() works
        // Even though WebSocket isn't ready yet, IndexedDB is the source of truth
        // This prevents "Read-only mode" placeholder during initialization
        // Message count may be 0 for Tab 2 (timing), state machine will handle sync
        const userEmail = this.connectionManager.getUserIdentifier() || 'pending';
        this.conversationManager.restoreConversation(
          existingConversationId,
          this.chatMessages.length,
          userEmail
        );

        console.log('[CHAT-MAIN] ✅ Conversation registered with ConversationManager (RESTORED state)');

        // Subscribe to state changes
        // 🔥 FIX (FIN-0064): Check for bot messages AFTER backend sync (when state becomes SYNCED)
        this.subscribeToConversationStateChanges(existingConversationId);
      }
    } catch (error) {
      console.error('[CHAT-MAIN] Failed to restore from IndexedDB:', error);
      // Still show UI even if restoration fails
      this.isRestoringFromIndexedDB = false;
    }

    // WebSocket connection happens in PARALLEL (doesn't block UI)
    this.connectionSubscription = this.connectionManager.websocketAvailable$
      .subscribe(() => {
        // WebSocket is ready - now we can set up real-time subscriptions
        const userEmail = this.connectionManager.getUserIdentifier();
        // FIN-0049: After FIN-0045, getUserIdentifier() returns actual email, not clientId
        if (!userEmail || userEmail === 'pending' || userEmail === '') {
          console.log('[CHAT-MAIN] Waiting for userEmail, current:', userEmail);

          setTimeout(() => {
            const updatedUserEmail = this.connectionManager.getUserIdentifier();
            if (updatedUserEmail && updatedUserEmail !== 'pending' && updatedUserEmail !== '') {
              console.log('[CHAT-MAIN] UserEmail now available:', updatedUserEmail);
              this.initializeWebSocketSubscriptions();
            }
          }, 1000);
          return;
        }

        this.initializeWebSocketSubscriptions();
      }, error => {
        this.errorTracking.trackWebSocketError('subscription_error', 'Error subscribing to WebSocket status', {
          error: String(error)
        });
      });

    // Add timeout for WebSocket connection establishment
    setTimeout(() => {
      if (this.hasInitializedChat) {
        return;
      }

      this.connectionManager.websocketAvailable$.subscribe(isConnected => {
        if (!isConnected) {
          this.errorTracking.trackError('websocket_timeout', 'WebSocket connection not established within timeout');
          try {
            this.initializeWebSocketSubscriptions();
          } catch (error) {
            this.errorTracking.trackError('fallback_chat_initialization', error);
          }
        }
      }).unsubscribe();
    }, 5000);

    // Ensure we have a connection (happens in background)
    try {
      this.connectionManager.establishConnection();
    } catch (error) {
      this.errorTracking.trackError('connection_establishment', error);
    }

    // Track component initialization complete
    this.performanceMetrics.markEnd('chat_component_init', 'chat_component_initialized');
  }

  ngAfterViewInit(): void {
    // Track when the view is fully initialized
    this.performanceMetrics.trackMilestone('chat_view_initialized');

    // Start tracking time to first bot message
    this.performanceMetrics.markStart('time_to_first_bot_message');

    // Add scroll event listener to track user scroll position
    if (this.conversationContainer) {
      const element = this.conversationContainer.nativeElement;
      element.addEventListener('scroll', () => {
        this.checkScrollPosition();
      });

      // Set up ResizeObserver to detect when content height changes
      // This handles: "Thinking..." state, progress portal, streaming content, trace UI
      // Debounced with requestAnimationFrame to prevent layout thrashing
      this.resizeObserver = new ResizeObserver(() => {
        // Only autoscroll if user hasn't manually scrolled up
        if (!this.isUserScrolledUp) {
          // Debounce with requestAnimationFrame - only one scroll per frame
          if (this.pendingScrollFrame === null) {
            this.pendingScrollFrame = requestAnimationFrame(() => {
              this.scrollToBottom(false);
              this.pendingScrollFrame = null;
            });
          }
        }
      });

      // Observe the conversation container for height changes
      this.resizeObserver.observe(element);
    }

    // Scroll to bottom on initial load if messages exist
    setTimeout(() => {
      if (this.chatMessages.length > 0) {
        this.scrollToBottom(true); // Force initial scroll
      }
    }, 300);
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
      this.messagesSubscription = null;
    }

    if (this.lastBotMessageSubscription) {
      this.lastBotMessageSubscription.unsubscribe();
      this.lastBotMessageSubscription = null;
    }

    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
      this.connectionSubscription = null;
    }

    if (this.connectionStatusSubscription) {
      this.connectionStatusSubscription.unsubscribe();
      this.connectionStatusSubscription = null;
    }

    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clean up pending scroll frame
    if (this.pendingScrollFrame !== null) {
      cancelAnimationFrame(this.pendingScrollFrame);
      this.pendingScrollFrame = null;
    }

    // Clean up connecting timer
    if (this.connectingTimer) {
      clearTimeout(this.connectingTimer);
      this.connectingTimer = null;
    }

    // Clean up state check interval
    if (this.stateCheckInterval) {
      clearInterval(this.stateCheckInterval);
      this.stateCheckInterval = null;
    }
  }

  getMessages(): void {
    this.messagesSubscription = this.chatService.getMessages().subscribe((data: Array<ChatMessage>) => {
      const timestamp = new Date().toISOString();
      const previousLength = this.chatMessages.length;

      // CRITICAL: Log every message array change - this shows when Angular will update DOM
      console.log(`[${timestamp}] [CHAT-MAIN] 📨 Messages subscription fired: ${previousLength} -> ${data.length} messages`);

      if (data.length > 0) {
        const previews = data.map(m => {
          const content = m.content.getValue();
          return `[${m.bot ? 'BOT' : 'USER'}] ${content.substring(0, 30)}...`;
        });
        console.log(`[${timestamp}] [CHAT-MAIN] Message previews:`, previews);
      }

      this.chatMessages = data;
      console.log(`[${timestamp}] [CHAT-MAIN] ✅ this.chatMessages updated. Angular change detection will run next.`);

      // 🔥 FIX: Hide loading state when messages arrive (prevents content layout shift)
      // Don't wait for restorationComplete$ - wait for actual message data
      if (this.isRestoringFromIndexedDB && data.length > 0) {
        console.log(`[${timestamp}] [CHAT-MAIN] ✅ Messages loaded, hiding loading state (${data.length} messages)`);
        this.isRestoringFromIndexedDB = false;
      }


      // If this is the first time we're getting messages, track it
      if (previousLength === 0 && data.length > 0) {
        this.performanceMetrics.trackMilestone('first_messages_received', {
          messageCount: data.length
        });
      }

      // Check if there's a bot message in the data
      if (!this.hasSeenFirstBotMessage) {
        const hasBotMessage = data.some(msg => msg.bot && msg.content.getValue().trim().length > 0);

        if (hasBotMessage) {
          this.hasSeenFirstBotMessage = true;

          // Mark the end of time to first bot message
          this.performanceMetrics.markEnd(
            'time_to_first_bot_message',
            'first_bot_message_displayed',
            { messageCount: data.length }
          );
        }
      }

      // Scroll to bottom when new messages arrive
      if (data.length > previousLength) {
        // Check if this is a user message (they just sent it) or bot response
        const latestMessage = data[data.length - 1];
        const isUserMessage = !latestMessage?.bot;

        // Update new message indicator
        if (data.length > this.lastSeenMessageCount) {
          this.hasNewMessages = true;
        }

        // Scroll to bottom for new messages (force for user messages)
        this.scrollToBottom(isUserMessage);
      }

      // ResizeObserver handles auto-scroll on content changes
      // No need for content.subscribe() - it causes redundant scroll operations and layout thrashing
    }, error => {
      this.errorTracking.trackError('message_subscription', error);
    });
  }

  /**
   * Scrolls the conversation container to the bottom
   * Uses instant scroll for reliability - smooth scroll is unreliable for programmatic scrolling
   * @param force - If true, scrolls even if user has scrolled up
   */
  scrollToBottom(force: boolean = false): void {
    if (!this.conversationContainer) {
      console.log('[SCROLL] No conversation container');
      return;
    }

    const element = this.conversationContainer.nativeElement;
    const { scrollTop, scrollHeight, clientHeight } = element;

    console.log('[SCROLL] scrollToBottom called', {
      force,
      isUserScrolledUp: this.isUserScrolledUp,
      scrollTop,
      scrollHeight,
      clientHeight,
      distanceFromBottom: scrollHeight - scrollTop - clientHeight
    });

    // Only auto-scroll if user is near the bottom, or if forced
    if (force || !this.isUserScrolledUp) {
      element.scrollTo({
        top: element.scrollHeight,
        behavior: 'instant'
      });

      console.log('[SCROLL] Scrolled to bottom', {
        newScrollTop: element.scrollTop,
        newScrollHeight: element.scrollHeight
      });

      this.isUserScrolledUp = false;
      this.hasNewMessages = false;
      this.lastSeenMessageCount = this.chatMessages.length;
    } else {
      console.log('[SCROLL] Skipped scroll (user scrolled up)');
    }
  }

  private checkScrollPosition(): void {
    if (this.conversationContainer) {
      const element = this.conversationContainer.nativeElement;
      const { scrollTop, scrollHeight, clientHeight } = element;

      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const wasScrolledUp = this.isUserScrolledUp;

      // Check if user is near the bottom (within threshold)
      this.isUserScrolledUp = distanceFromBottom > this.scrollThreshold;

      if (wasScrolledUp !== this.isUserScrolledUp) {
        console.log('[SCROLL] Scroll position changed', {
          isUserScrolledUp: this.isUserScrolledUp,
          distanceFromBottom,
          threshold: this.scrollThreshold
        });
      }
    }
  }

  /**
   * Track the first user interaction with the chat
   * Call this from the template on click or other interaction
   */
  trackFirstUserInteraction(): void {
    if (!this.firstUserInteractionTracked) {
      this.firstUserInteractionTracked = true;
      this.performanceMetrics.trackMilestone('first_user_interaction');
    }
  }

  /**
   * Initialize WebSocket subscriptions after connection is established
   * This sets up real-time communication for the active conversation
   */
  private async initializeWebSocketSubscriptions(): Promise<void> {
    // Prevent multiple initializations
    if (this.hasInitializedChat) {
      console.log('[CHAT-MAIN] WebSocket subscriptions already initialized, skipping duplicate');
      return;
    }

    const userEmail = this.connectionManager.getUserIdentifier();

    // Validate we have a valid userEmail (FIN-0049: After FIN-0045, this is an email address, not clientId)
    if (!userEmail || userEmail === 'pending' || userEmail === '') {
      console.log('[CHAT-MAIN] Cannot initialize WebSocket subscriptions, userEmail not available:', userEmail);
      return;
    }

    try {
      // Mark that we've initialized to prevent duplicates
      this.hasInitializedChat = true;

      // Track WebSocket connection established milestone
      this.performanceMetrics.trackMilestone('websocket_connected');

      // 🔥 FIX (FIN-0068): Eliminate Path B (new conversation creation)
      // ALL conversation creation now happens in ngOnInit()
      // This method ONLY handles WebSocket-related setup for existing conversations

      // Check if we already have a conversation from IndexedDB restoration
      const existingConversationId = this.conversationManager.getActiveConversationId();

      if (existingConversationId) {
        // ✅ RESTORED state - Already registered in ngOnInit()
        // State machine handles backend sync check automatically
        const messageCount = this.chatMessages.length;

        console.log('[CHAT-MAIN] WebSocket ready for restored conversation', {
          conversationId: existingConversationId,
          messageCount
        });

        // Update client ID now that WebSocket is connected
        // The conversation was already restored in ngOnInit(), just update the client ID
        const state = this.conversationManager.getConversationState(existingConversationId);
        if (state && state.kind === 'RESTORED') {
          // State machine already initiated sync check, just log
          console.log('[CHAT-MAIN] ✅ Conversation already registered, sync check in progress');
        }

        return;
      }

      // 🔥 NEW: First-time user (no conversation ID in localStorage)
      // Create new DRAFT conversation
      this.conversationId = this.conversationManager.generateConversationId();

      console.log('[CHAT-MAIN] Creating new DRAFT conversation (first-time user)', {
        conversationId: this.conversationId,
        userEmail
      });

      // Create DRAFT conversation via state machine
      this.conversationManager.createConversation(this.conversationId, userEmail);

      // Set as active conversation
      this.conversationManager.setActiveConversationId(this.conversationId);

      // Subscribe to state changes to detect ORPHANED state
      this.subscribeToConversationStateChanges(this.conversationId);

      // Initialize chat (bot will send greeting)
      this.chatService.initializeChatConversation(this.conversationId);

      console.log('[CHAT-MAIN] New DRAFT conversation initialized (first-time user)');
    } catch (error) {
      // Reset the flag on error so initialization can be retried
      this.hasInitializedChat = false;
      this.errorTracking.trackError('websocket_initialization', error);
    }
  }

  // ========================================
  // ORPHANED CONVERSATION HANDLING
  // ========================================

  /**
   * Subscribe to conversation state changes to detect ORPHANED state
   * Continuously polls to update UI when state changes
   */
  private subscribeToConversationStateChanges(conversationId: string): void {
    // Clear any existing interval first
    if (this.stateCheckInterval) {
      clearInterval(this.stateCheckInterval);
    }

    // Poll for state changes continuously
    this.stateCheckInterval = setInterval(() => {
      const state = this.conversationManager.getConversationState(conversationId);

      if (!state) {
        // Conversation no longer exists - stop checking
        if (this.stateCheckInterval) {
          clearInterval(this.stateCheckInterval);
          this.stateCheckInterval = null;
        }
        return;
      }

      // Update orphaned state based on current conversation state
      if (state.kind === 'ORPHANED') {
        if (!this.isOrphaned) {
          // State changed to ORPHANED
          this.isOrphaned = true;
          this.orphanReason = this.conversationManager.getStateDescription(conversationId);

          console.log('[CHAT-MAIN] ⚠️ Conversation is ORPHANED', {
            conversationId,
            reason: state.reason
          });
        }
      } else if (state.kind === 'SYNCED' || state.kind === 'DRAFT') {
        // 🔥 FIX (FIN-0064): Bot message check moved to message subscription (getMessages)
        // This ensures we check AFTER messages are actually updated by backend sync

        if (this.isOrphaned) {
          // State changed from ORPHANED to SYNCED/DRAFT
          this.isOrphaned = false;
          this.orphanReason = '';

          console.log('[CHAT-MAIN] ✅ Conversation is now ' + state.kind, {
            conversationId
          });
        }
      }
    }, 500); // Check every 500ms
  }

  /**
   * Check if conversation can send messages
   * Optimistic: Returns true during initialization for better UX
   */
  canSendMessages(): boolean {
    if (!this.conversationId) {
      // During initialization, be optimistic - don't block the user
      // The conversation will be available soon (within ~100ms)
      return true;
    }

    return this.conversationManager.canSendMessages(this.conversationId);
  }

  /**
   * Upload orphaned conversation to backend (user chose to resurrect)
   */
  async uploadOrphanedConversation(): Promise<void> {
    const conversationId = this.conversationId;
    if (!conversationId) {
      console.error('[CHAT-MAIN] Cannot upload - no conversation ID');
      return;
    }

    try {
      // Resurrect the orphaned conversation (ORPHANED → DRAFT)
      this.conversationManager.resurrectOrphanedConversation(conversationId);

      // Get conversation data from IndexedDB
      const messages = this.chatMessages;

      if (!messages || messages.length === 0) {
        console.log('[CHAT-MAIN] No messages to upload');
        return;
      }

      // Get current user email
      const userAuthInfo = await firstValueFrom(this.authService.userAuthInfo$);

      // Upload to backend
      const conversation = {
        conversationId,
        userEmail: userAuthInfo?.email,
        messages: messages.map(msg => ({
          messageId: msg.messageId,
          conversationId: msg.conversationId,
          content: msg.content.getValue(),
          bot: msg.bot,
          timestamp: msg.timestamp,
          sequenceNumber: (msg as any).sequenceNumber || 0
        }))
      };

      await firstValueFrom(
        this.http.post(`/api/chat/conversation/${conversationId}`, conversation)
      );

      // Mark as synced (DRAFT → SYNCED)
      this.conversationManager.markConversationSynced(conversationId, messages.length);

      console.log('[CHAT-MAIN] ✅ Orphaned conversation uploaded and synced', {
        conversationId,
        messageCount: messages.length
      });
    } catch (error) {
      console.error('[CHAT-MAIN] ❌ Failed to upload orphaned conversation', error);
      this.errorTracking.trackError('orphaned_upload_failed', error);
    }
  }

  /**
   * Delete orphaned conversation from local storage
   */
  async deleteOrphanedConversation(): Promise<void> {
    const conversationId = this.conversationId;
    if (!conversationId) {
      return;
    }

    if (confirm('Delete this conversation from this device? This cannot be undone.')) {
      console.log('[CHAT-MAIN] Deleting orphaned conversation', { conversationId });

      // Clear the orphaned flag FIRST (so banner disappears immediately)
      this.isOrphaned = false;
      this.orphanReason = '';

      // Clear messages from IndexedDB
      // 🔥 FIX: Must await clearMessages() since it's async
      await this.chatService.clearMessages();

      // Clear conversation state
      this.conversationManager.clearAllConversations();

      // Create a new conversation
      // 🔥 FIX: createNewConversation is now async, must await
      await this.createNewConversation();
    }
  }

  simulateProgressUpdate(): void {
    const fakeUpdate: ProgressUpdate = {
      operationId: 'debug-op-' + Math.floor(Math.random() * 10000),
      type: 'debug',
      title: 'Debug Progress',
      message: 'Simulated progress update',
      progress: Math.floor(Math.random() * 100),
      timestamp: new Date().toISOString()
    };
    this.progressTrackingService.registerProgressUpdate(fakeUpdate);
  }

  /**
   * Determine if connection status banner should be shown
   * Shows for: CONNECTING (after 2s delay), RECONNECTING, or FAILED states
   */
  shouldShowConnectionStatus(): boolean {
    return (this.connectionState === ConnectionState.CONNECTING && this.showConnectingBanner) ||
           this.connectionState === ConnectionState.RECONNECTING ||
           this.connectionState === ConnectionState.FAILED;
  }

  /**
   * Get connection status message based on current state
   */
  getConnectionStatusMessage(): string {
    switch (this.connectionState) {
      case ConnectionState.CONNECTING:
        return 'Connecting...';
      case ConnectionState.RECONNECTING:
        return `Reconnecting... (${this.reconnectAttempt}/${this.maxAttempts})`;
      case ConnectionState.FAILED:
        return 'Connection lost';
      default:
        return '';
    }
  }

  /**
   * Get CSS classes for connection status based on current state
   */
  getConnectionStatusClass(): string {
    const baseClasses = 'text-sm font-light';
    switch (this.connectionState) {
      case ConnectionState.CONNECTING:
        return `${baseClasses} text-gray-400`; // Same as "Signing in..."
      case ConnectionState.RECONNECTING:
        return `${baseClasses} text-yellow-600`; // Warning but still trying
      case ConnectionState.FAILED:
        return `${baseClasses} text-red-600`; // Error, needs attention
      default:
        return baseClasses;
    }
  }

  /**
   * Manual reconnection when auto-retry fails
   */
  manualReconnect(): void {
    this.isRetrying = true;
    this.reconnectAttempt = 0; // Reset attempts for manual retry

    // Trigger reconnection through connection manager
    try {
      this.connectionManager.establishConnection();

      // Set timeout to reset retry flag if connection doesn't succeed
      setTimeout(() => {
        if (this.connectionState !== ConnectionState.CONNECTED) {
          this.isRetrying = false;
        }
      }, 10000); // 10 second timeout
    } catch (error) {
      this.isRetrying = false;
      this.errorTracking.trackError('manual_reconnect_failed', error);
    }
  }

  /**
   * Create a new conversation
   * Clears current messages and initializes a fresh conversation with bot greeting
   */
  async createNewConversation(): Promise<void> {
    const startTime = new Date().toISOString();
    const startTimestamp = Date.now();
    console.log(`[${startTime}] [CHAT-MAIN] 🔄 Creating new conversation - START`);

    // Clear all existing messages and conversation state
    // 🔥 FIX: Must await clearMessages() since it's async (IndexedDB operations)
    // Without await, race condition causes old messages to persist
    console.log(`[${new Date().toISOString()}] [CHAT-MAIN] Awaiting clearMessages()...`);
    await this.chatService.clearMessages();
    const afterClearTime = new Date().toISOString();
    const clearDuration = Date.now() - startTimestamp;
    console.log(`[${afterClearTime}] [CHAT-MAIN] ✅ Messages cleared (took ${clearDuration}ms)`);
    console.log(`[${afterClearTime}] [CHAT-MAIN] chatMessages.length = ${this.chatMessages.length}`);

    // Generate a new conversation ID
    const newConversationId = this.conversationManager.generateConversationId();
    console.log(`[${new Date().toISOString()}] [CHAT-MAIN] Generated new conversation ID: ${newConversationId}`);

    // 🔥 FIX: Register conversation IMMEDIATELY to prevent race condition
    // Must be done BEFORE initializeChatConversation() so bot response chunks aren't filtered out
    const userEmail = this.connectionManager.getUserIdentifier();
    this.conversationManager.createConversation(newConversationId, userEmail);
    console.log(`[${new Date().toISOString()}] [CHAT-MAIN] Registered conversation in ConversationManager`);

    // Set the new conversation ID as active
    this.conversationManager.setActiveConversationId(newConversationId);

    // Update component's conversation ID
    this.conversationId = newConversationId;

    // Subscribe to state changes to detect ORPHANED state
    this.subscribeToConversationStateChanges(newConversationId);

    // Initialize the chat conversation (bot will send greeting)
    // This sends an empty message array to backend, which triggers bot initialization
    const initTime = new Date().toISOString();
    console.log(`[${initTime}] [CHAT-MAIN] Calling initializeChatConversation() - bot greeting will be sent`);
    this.chatService.initializeChatConversation(newConversationId);

    const endTime = new Date().toISOString();
    const totalDuration = Date.now() - startTimestamp;
    console.log(`[${endTime}] [CHAT-MAIN] ✅ New conversation initialized (total: ${totalDuration}ms)`);
    console.log(`[${endTime}] [CHAT-MAIN] isRestoringFromIndexedDB = ${this.isRestoringFromIndexedDB}`);
    console.log(`[${endTime}] [CHAT-MAIN] 🎯 Angular will now render any new messages via *ngIf="!isRestoringFromIndexedDB"`);
  }

  /**
   * Copy the entire DOM to clipboard for debugging purposes
   */
  copyDOMToClipboard(): void {
    try {
      // Get the entire HTML document
      const domString = document.documentElement.outerHTML;

      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(domString).then(() => {
          console.log('[DEBUG] DOM copied to clipboard (modern API)');
          alert('DOM copied to clipboard!');
        }).catch(err => {
          console.error('[DEBUG] Failed to copy DOM with modern API:', err);
          // Fallback to textarea method
          this.copyUsingTextarea(domString);
        });
      } else {
        // Use fallback method directly
        console.log('[DEBUG] Clipboard API not available, using fallback');
        this.copyUsingTextarea(domString);
      }
    } catch (error) {
      console.error('[DEBUG] Error copying DOM:', error);
      alert('Error copying DOM to clipboard: ' + error);
    }
  }

  /**
   * Fallback method to copy text using a temporary textarea
   */
  private copyUsingTextarea(text: string): void {
    try {
      // Create a temporary textarea
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);

      // Select and copy
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');

      // Clean up
      document.body.removeChild(textarea);

      if (successful) {
        console.log('[DEBUG] DOM copied to clipboard (fallback method)');
        alert('DOM copied to clipboard!');
      } else {
        console.error('[DEBUG] execCommand copy failed');
        alert('Failed to copy DOM to clipboard');
      }
    } catch (error) {
      console.error('[DEBUG] Fallback copy failed:', error);
      alert('Failed to copy DOM: ' + error);
    }
  }

  // ========================================
  // CHAT HISTORY SIDEBAR (FIN-0102)
  // ========================================

  /**
   * Toggle the chat history sidebar
   */
  toggleHistorySidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;

    // Load conversations when opening sidebar
    if (this.isSidebarOpen) {
      this.conversationListService.loadConversations();
    }
  }

  /**
   * Handle conversation selection from sidebar
   */
  async onConversationSelected(conversationId: string): Promise<void> {
    console.log('[CHAT-MAIN] Conversation selected from sidebar:', conversationId);

    // Check if this is already the active conversation
    if (conversationId === this.conversationId) {
      console.log('[CHAT-MAIN] Already active conversation, skipping');
      return;
    }

    // Show loading state
    this.isRestoringFromIndexedDB = true;

    try {
      // Clear current messages
      await this.chatService.clearMessages();

      // Set the new conversation as active
      this.conversationId = conversationId;
      this.conversationManager.setActiveConversationId(conversationId);

      // Load messages from backend
      const userEmail = this.connectionManager.getUserIdentifier();
      this.conversationManager.restoreConversation(conversationId, 0, userEmail || 'pending');

      // Subscribe to state changes
      this.subscribeToConversationStateChanges(conversationId);

      console.log('[CHAT-MAIN] Switched to conversation:', conversationId);
    } catch (error) {
      console.error('[CHAT-MAIN] Failed to switch conversation:', error);
      this.errorTracking.trackError('conversation_switch_failed', error);
    } finally {
      // Hide loading state after a brief delay to allow messages to load
      setTimeout(() => {
        this.isRestoringFromIndexedDB = false;
      }, 500);
    }
  }

  /**
   * Handle new conversation request from sidebar
   */
  onNewConversationFromSidebar(): void {
    console.log('[CHAT-MAIN] New conversation requested from sidebar');
    this.createNewConversation();
  }
}
